import {gsap} from "gsap/all";

// The air burning around the ship as it enters the Martian atmosphere. Two
// canvases sit on either side of the ship art: the glow behind it, holding the
// hot rim of the heat shield and the sparks coming off it, and the haze in front,
// the cooled smoke that has drifted back over the dish.
//
// Canvas instead of SVG, because smoke in SVG means feTurbulence, which is redrawn
// pixel by pixel every frame it moves and drops the kiosk to a few frames a second.
// Stacking soft sprites gives the same uneven edge for a few dozen drawImage calls.
//
// The effect builds once, on the way into the entry, and runs until the descent
// puts it out. It ignores the game state: nothing here reads the speed gauge, the
// alignment or the ship position. Both canvases live inside the ship assembly, so
// they follow it through every move of the run, scaled by the same transforms.
export class ShipHeatGlow {

  // Anything measured below can be overridden through options, which is what a
  // second ship drawn at another size or another angle would take: the five
  // numbers of the dish and the viewBox they are measured in.
  constructor($host, options = {}) {
    this.$host = $host;

    this.$glow = $host.find("canvas.ship-heat-glow");
    this.$haze = $host.find("canvas.ship-heat-haze");

    this.glow = this.$glow.length ? this.$glow.get(0).getContext("2d") : null;
    this.haze = this.$haze.length ? this.$haze.get(0).getContext("2d") : null;

    // The frame of the capsule art the effect is measured in. Every position
    // below is in these units and goes through the same fit, see sizeCanvases,
    // so the burn stays glued to the ship at whatever size the run is flying it.
    this.viewBoxWidth = 1067;
    this.viewBoxHeight = 1067;

    // The dish of the ship, measured off that art: its center, its rotation and
    // its two semi axes. Everything drawn here is placed against this ellipse, so if
    // the ship art changes these five numbers are the only ones to remeasure.
    // The turn is zero because the assembly holding the canvases is what carries
    // the tilt of the ship.
    this.dishX = 533;
    this.dishY = 478;
    this.dishAngle = 0;
    this.dishA = 457;
    this.dishB = 244;

    // The leading half of the rim, the one facing the air. Measured from the
    // trailing tip around the underside, so 90 degrees is straight into the wind and
    // both ends are the tips of the dish.
    this.arcFrom = -5 * Math.PI / 180;
    this.arcTo = 197 * Math.PI / 180;

    // The part of the frame the burn can reach, far bigger than the art itself:
    // the shield spans nearly the whole of it and the trail runs a long way past
    // it. Anything this cuts off is a visible straight edge across the fire. It
    // is still cut down rather than covering the page, which would mean clearing
    // eight million pixels twice a frame.
    this.box = {x: -450, y: -1350, width: 2000, height: 2800};

    // That box is a lot of pixels to clear twice a frame. Smoke has nothing fine
    // in it, so it is drawn at half resolution and stretched, which costs four
    // times fewer pixels and looks the same. Raise it if the burn ever looks
    // banded on the kiosk.
    this.maxPixelRatio = 0.5;

    // How hot the burn ever gets: everything drawn is multiplied by the current
    // intensity, and this is where the build takes it. Under 1 the whole effect
    // is held back, puff count included, rather than any one part being dimmed.
    this.maxIntensity = 0.65;

    // Puffs per second off the rim once the burn is at full heat.
    this.emitPerSecond = 400;

    // How long a puff lives, and the age at which it stops being fire behind the
    // ship and becomes smoke drifting over it.
    this.lifeMin = 1.1;
    this.lifeMax = 3.2;
    this.hazeAt = 0.17;

    // How fast a puff leaves the rim and how quickly it is carried back, both in
    // viewBox units a second. Together with the life above, this is what decides
    // how far the trail reaches. Kept as options because the stage art and the
    // capsule art are drawn at very different scales, so the same numbers would
    // throw a plume twice the size on the smaller one.
    this.radialSpeed = {min: 0.11, max: 0.41};
    this.driftSpeed = {min: 165, max: 900};

    // How solid a puff is drawn, before its own fade and the intensity of the
    // build scale it down. This is the opacity of the fire itself, where the rim
    // band below has its own.
    this.puffAlpha = {min: 0.06, max: 0.47};

    // What is left of that once a puff has cooled and crossed to the canvas in
    // front of the ship, where it is smoke washing over the hull rather than
    // fire coming off the shield.
    this.hazeAlpha = 0.38;

    // How much a puff is drawn out along its own path, at birth and at the end of
    // its life. A round blob reads as a cloud, a long one as air being torn past
    // the shield, which is what the trail is.
    this.stretchMin = 2.2;
    this.stretchMax = 5.4;

    // The steady glow on the rim, under the puffs. Enough of them to overlap into
    // one band instead of reading as beads.
    this.rimCount = 38;
    this.rimSize = 250;

    // How hard the band and its core burn, straight into the wind, before the
    // window across the arc and the intensity of the build scale them down.
    this.rimAlpha = 0.28;
    this.rimCoreAlpha = 0.97;

    // Warmest to coldest. A puff walks down this list as it ages, which is what
    // turns fire into smoke without any color math per frame. The first two are
    // near white: stacked additively, that is what makes the air in front of the
    // shield burn out rather than merely glow.
    this.palette = ["#FFF7EC", "#FFE3BE", "#FBBE7E", "#EE8F3E", "#CE5C26", "#9C3E19", "#642F15"];

    Object.assign(this, options);

    this.sprites = this.buildSprites();
    this.particles = [];

    // Everything drawn is multiplied by this, so the build is a single tween on one
    // number and the ambient state is just it sitting at maxIntensity.
    this.intensity = 0;

    // Wireframe view, see drawDebug. Off in production, turned on by the
    // ShipHeatGlowDebug.js panel.
    this.debug = false;

    this.running = false;
    this.paused = false;
    this.emitCarry = 0;
    this.elapsed = 0;

    this.enterTimeline = null;
    this.exitTimeline = null;

    this.tick = this.tick.bind(this);
    this.sizeCanvases = this.sizeCanvases.bind(this);

    window.addEventListener("resize", this.sizeCanvases);

    this.sizeCanvases();
    this.reset();
  }

  // One soft blob per palette color, drawn once and stamped over and over. Tinting
  // them up front means a puff is never recolored at draw time, it just picks the
  // sprite matching how cold it has gone.
  buildSprites() {
    return this.palette.map((color) => {
      const size = 128;
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;

      const ctx = canvas.getContext("2d");
      const gradient = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);

      // Tighter and hotter in the middle than a plain falloff, so stacking a few
      // of them piles up into a bright core instead of an even wash.
      gradient.addColorStop(0, color);
      gradient.addColorStop(0.3, this.withAlpha(color, 0.72));
      gradient.addColorStop(0.65, this.withAlpha(color, 0.2));
      gradient.addColorStop(1, this.withAlpha(color, 0));

      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, size, size);

      return canvas;
    });
  }

  withAlpha(hex, alpha) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  // The canvases cover the same box as the stage ship SVG and have to land on the
  // same pixels, so they repeat what a viewBox does: scale to whichever side runs
  // out first, then center the leftover.
  sizeCanvases() {
    const width = this.$host.width();
    const height = this.$host.height();
    if (!width || !height) return;

    this.ratio = Math.min(window.devicePixelRatio || 1, this.maxPixelRatio);

    this.scale = Math.min(width / this.viewBoxWidth, height / this.viewBoxHeight);
    this.originX = (width - this.viewBoxWidth * this.scale) / 2;
    this.originY = (height - this.viewBoxHeight * this.scale) / 2;

    const cssWidth = this.box.width * this.scale;
    const cssHeight = this.box.height * this.scale;

    [this.$glow, this.$haze].forEach(($canvas) => {
      if (!$canvas.length) return;

      const canvas = $canvas.get(0);
      canvas.width = Math.round(cssWidth * this.ratio);
      canvas.height = Math.round(cssHeight * this.ratio);

      $canvas.css({
        left: `${this.originX + this.box.x * this.scale}px`,
        top: `${this.originY + this.box.y * this.scale}px`,
        width: `${cssWidth}px`,
        height: `${cssHeight}px`
      });
    });
  }

  // A point on the rim, or off it when radial is not zero: 0 is the rim itself,
  // positive is out into the air, negative is in over the shield.
  rimPoint(theta, radial, driftX, driftY) {
    const localX = this.dishA * (1 + radial) * Math.cos(theta) + (driftX || 0);
    const localY = this.dishB * (1 + radial) * Math.sin(theta) + (driftY || 0);

    const cos = Math.cos(this.dishAngle);
    const sin = Math.sin(this.dishAngle);

    return {
      x: this.dishX + localX * cos - localY * sin,
      y: this.dishY + localX * sin + localY * cos
    };
  }

  // viewBox units to pixels inside the canvas box. Nothing here accounts for the
  // ship being steered, since the box holding the canvases is what moves.
  toCanvas(point) {
    return {
      x: (point.x - this.box.x) * this.scale * this.ratio,
      y: (point.y - this.box.y) * this.scale * this.ratio
    };
  }

  spawn() {
    const spread = this.arcTo - this.arcFrom;

    // Biased toward the middle of the arc, where the air piles up hardest, by
    // averaging two rolls instead of taking one flat.
    const roll = (Math.random() + Math.random()) / 2;
    const theta = this.arcFrom + roll * spread;

    this.particles.push({
      theta: theta,
      // Straddling the rim so the band has depth instead of sitting outside it.
      radial: -0.12 + Math.random() * 0.22,
      driftX: 0,
      driftY: 0,
      // Out into the air, and backward over the dish, which is what carries the
      // cooled smoke across the hull.
      radialVel: this.radialSpeed.min + Math.random() * (this.radialSpeed.max - this.radialSpeed.min),
      thetaVel: (Math.random() - 0.5) * 0.5,
      driftVelY: -(this.driftSpeed.min + Math.random() * (this.driftSpeed.max - this.driftSpeed.min)),
      age: 0,
      life: this.lifeMin + Math.random() * (this.lifeMax - this.lifeMin),
      size: 130 + Math.random() * 170,
      alpha: this.puffAlpha.min + Math.random() * (this.puffAlpha.max - this.puffAlpha.min),
      // How fast this one gives up its heat, so the puffs never cool in step.
      cool: 0.75 + Math.random() * 0.5,
      spin: (Math.random() - 0.5) * 1.4
    });
  }

  // Held with the rest of the run while the "still there?" modal is up. The
  // particles are moved by hand here, so nothing else can freeze them.
  setPaused(paused) {
    this.paused = paused;
  }

  tick(time, deltaMS) {
    if (!this.running || this.paused) return;

    const dt = Math.min(deltaMS / 1000, 0.05);
    this.elapsed += dt;

    this.emitCarry += this.emitPerSecond * this.intensity * dt;
    while (this.emitCarry >= 1) {
      this.emitCarry -= 1;
      this.spawn();
    }

    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];

      p.age += dt;
      if (p.age >= p.life) {
        this.particles.splice(i, 1);
        continue;
      }

      p.radial += p.radialVel * dt;
      p.theta += p.thetaVel * dt;
      p.driftY += p.driftVelY * dt;
      p.driftX += p.spin * 30 * dt;
    }

    this.draw();
  }

  draw() {
    if (!this.glow || !this.haze) return;

    const pw = this.$glow.get(0).width;
    const ph = this.$glow.get(0).height;

    this.glow.clearRect(0, 0, pw, ph);
    this.haze.clearRect(0, 0, pw, ph);

    if (this.debug) {
      this.drawDebug();
      return;
    }

    // Added on top of each other instead of painted over each other, so the air
    // goes white hot on its own where the puffs pile up.
    this.glow.globalCompositeOperation = "lighter";
    this.haze.globalCompositeOperation = "lighter";

    this.drawRim();
    this.drawParticles();
  }

  // The band on the rim, under everything else. Without it the burn would wink out
  // whenever the puffs thin out, and the shield would go dark.
  drawRim() {
    const spread = this.arcTo - this.arcFrom;

    for (let i = 0; i < this.rimCount; i++) {
      const t = i / (this.rimCount - 1);
      const theta = this.arcFrom + t * spread;

      // Brightest straight into the wind, fading toward both tips, with the forward
      // tip kept a little hotter than the trailing one.
      const window = Math.pow(Math.sin(Math.PI * t), 0.6) * (0.72 + 0.28 * Math.cos(theta));

      // A slow wander so the band never sits still. Cheap, because it only moves
      // where a sprite is stamped instead of redrawing pixels.
      const wander = Math.sin(this.elapsed * 1.7 + i * 0.9) * 0.05 +
        Math.sin(this.elapsed * 0.9 + i * 2.3) * 0.035;

      const point = this.rimPoint(theta, 0.02 + wander, 0, 0);
      const breath = 1 + Math.sin(this.elapsed * 2.1 + i * 1.3) * 0.18;

      const spriteIndex = t < 0.2 || t > 0.85 ? 3 : 2;
      this.stamp(this.glow, this.sprites[spriteIndex], point, this.rimSize * breath, window * this.rimAlpha * this.intensity);
      this.stamp(this.glow, this.sprites[0], point, this.rimSize * 0.45 * breath, window * this.rimCoreAlpha * this.intensity);
    }
  }

  drawParticles() {
    this.particles.forEach((p) => {
      const t = p.age / p.life;

      // Up fast, down slow, so a puff flares as it leaves the rim and takes its time
      // dying.
      const fade = Math.sin(Math.PI * Math.pow(t, 0.55));

      const cooled = Math.min(t * p.cool, 1);
      const spriteIndex = Math.min(Math.floor(cooled * this.palette.length), this.palette.length - 1);

      const point = this.rimPoint(p.theta, p.radial, p.driftX, p.driftY);
      const size = p.size * (0.55 + 1.25 * t);
      const alpha = fade * p.alpha * this.intensity;

      // Drawn out further the older it gets, so a puff leaves the rim as a bead
      // and is a long streak by the time it is behind the ship.
      const stretch = this.stretchMin + (this.stretchMax - this.stretchMin) * t;

      // Young and hot behind the ship, old and cold in front of it, which is how the
      // smoke washes over the hull without a second emitter.
      const ctx = t < this.hazeAt ? this.glow : this.haze;
      this.stamp(ctx, this.sprites[spriteIndex], point, size, alpha * (ctx === this.haze ? this.hazeAlpha : 1), this.flowAngle(p, point), stretch);
    });
  }

  // Which way a puff is travelling, from where it stood a moment ago. Walking
  // the same motion backwards costs one more rimPoint and needs no velocity
  // kept in viewBox units, which the polar position does not have.
  flowAngle(p, point) {
    const back = 0.08;
    const before = this.rimPoint(
      p.theta - p.thetaVel * back,
      p.radial - p.radialVel * back,
      p.driftX - p.spin * 30 * back,
      p.driftY - p.driftVelY * back
    );

    return Math.atan2(point.y - before.y, point.x - before.x);
  }

  // Round when nothing asks otherwise, and drawn out along `angle` when it does.
  // A stretched sprite is anchored near its leading edge, so the length trails
  // behind the puff instead of running ahead of it.
  stamp(ctx, sprite, point, size, alpha, angle = 0, stretch = 1) {
    if (alpha <= 0.002) return;

    const canvasPoint = this.toCanvas(point);
    const drawn = size * this.scale * this.ratio;

    ctx.globalAlpha = Math.min(alpha, 1);

    if (stretch <= 1) {
      ctx.drawImage(sprite, canvasPoint.x - drawn / 2, canvasPoint.y - drawn / 2, drawn, drawn);
      return;
    }

    const long = drawn * stretch;

    ctx.save();
    ctx.translate(canvasPoint.x, canvasPoint.y);
    ctx.rotate(angle);
    ctx.drawImage(sprite, -long * 0.78, -drawn / 2, long, drawn);
    ctx.restore();
  }

  setDebug(on) {
    this.debug = !!on;
  }

  // Wireframe view of the same frame: the dish ellipse everything is measured
  // against, the arc the puffs come off, and one flat circle per particle at the
  // size its sprite would be stamped at. No glow, no fade and no additive blending,
  // so the geometry and the motion are actually readable. Solid circles are on the
  // back canvas, dashed ones have crossed over to the front canvas.
  drawDebug() {
    [this.glow, this.haze].forEach((ctx) => {
      ctx.globalCompositeOperation = "source-over";
      ctx.globalAlpha = 1;
      ctx.lineWidth = Math.max(1, 2 * this.scale * this.ratio);
    });

    this.debugDish();
    this.debugParticles();
  }

  debugDish() {
    const ctx = this.glow;

    // The whole ellipse, faint, then the leading arc on top of it. What lies
    // between the two magenta tips is the only part that ever emits.
    this.debugPath(ctx, 0, Math.PI * 2, "rgba(255, 255, 255, 0.35)", true);
    this.debugPath(ctx, this.arcFrom, this.arcTo, "#FF3EA5", false);

    // The center, and the two radial extremes a puff is born between, so the band
    // depth is visible as a ring rather than guessed from the numbers.
    this.debugCross(ctx, this.rimPoint(0, -1, 0, 0), "#3EF0FF");
    this.debugPath(ctx, this.arcFrom, this.arcTo, "rgba(62, 240, 255, 0.5)", true, -0.12);
    this.debugPath(ctx, this.arcFrom, this.arcTo, "rgba(62, 240, 255, 0.5)", true, 0.1);
  }

  debugParticles() {
    this.particles.forEach((p) => {
      const t = p.age / p.life;
      const onHaze = t >= this.hazeAt;
      const ctx = onHaze ? this.haze : this.glow;

      const point = this.rimPoint(p.theta, p.radial, p.driftX, p.driftY);
      const size = p.size * (0.55 + 1.25 * t);

      const cooled = Math.min(t * p.cool, 1);
      const spriteIndex = Math.min(Math.floor(cooled * this.palette.length), this.palette.length - 1);

      // Same color the sprite would have picked, so how fast a puff cools reads at
      // a glance, and the circle is the sprite's real footprint.
      this.debugCircle(ctx, point, size / 2, this.palette[spriteIndex], onHaze);
      this.debugCross(ctx, point, this.palette[spriteIndex]);
    });
  }

  // An ellipse arc walked in viewBox units, since rimPoint already handles the
  // rotation and the canvas has no arc that knows about it.
  debugPath(ctx, from, to, color, dashed, radial = 0) {
    const steps = 64;

    ctx.setLineDash(dashed ? [8, 8] : []);
    ctx.strokeStyle = color;
    ctx.beginPath();

    for (let i = 0; i <= steps; i++) {
      const theta = from + (to - from) * (i / steps);
      const point = this.toCanvas(this.rimPoint(theta, radial, 0, 0));

      i === 0 ? ctx.moveTo(point.x, point.y) : ctx.lineTo(point.x, point.y);
    }

    ctx.stroke();
    ctx.setLineDash([]);
  }

  debugCircle(ctx, point, radius, color, dashed) {
    const canvasPoint = this.toCanvas(point);

    ctx.setLineDash(dashed ? [4, 6] : []);
    ctx.strokeStyle = color;
    ctx.beginPath();
    ctx.arc(canvasPoint.x, canvasPoint.y, Math.max(1, radius * this.scale * this.ratio), 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);
  }

  debugCross(ctx, point, color) {
    const canvasPoint = this.toCanvas(point);
    const arm = 6 * this.ratio;

    ctx.setLineDash([]);
    ctx.strokeStyle = color;
    ctx.beginPath();
    ctx.moveTo(canvasPoint.x - arm, canvasPoint.y);
    ctx.lineTo(canvasPoint.x + arm, canvasPoint.y);
    ctx.moveTo(canvasPoint.x, canvasPoint.y - arm);
    ctx.lineTo(canvasPoint.x, canvasPoint.y + arm);
    ctx.stroke();
  }

  // Nothing showing, ready for a build. Also where a run that ended lands.
  reset() {
    this.stopAmbient();
    this.killTimelines();

    gsap.killTweensOf(this);
    gsap.killTweensOf(this.$glow);
    gsap.killTweensOf(this.$haze);

    this.intensity = 0;
    this.particles = [];
    this.emitCarry = 0;

    gsap.set([this.$glow, this.$haze], {autoAlpha: 0});

    if (this.glow && this.haze) {
      const canvas = this.$glow.get(0);
      this.glow.clearRect(0, 0, canvas.width, canvas.height);
      this.haze.clearRect(0, 0, canvas.width, canvas.height);
    }
  }

  // The ship entering the atmosphere: the air lights up around the shield and the
  // burn comes up to heat. Returns the timeline so the caller can drop it into a
  // longer transition and resolve on its end.
  buildEnter(durationSec = 2.4) {
    this.killTimelines();

    // Drawing starts with the build, so the first puffs are already on their way out
    // by the time the layers have faded up.
    this.sizeCanvases();
    this.startAmbient();

    this.enterTimeline = gsap.timeline();

    this.enterTimeline
      .to(this.$glow, {
        autoAlpha: 1,
        duration: durationSec * 0.7,
        ease: "power2.out"
      }, 0)
      .to(this.$haze, {
        autoAlpha: 1,
        duration: durationSec * 0.9,
        ease: "power2.out"
      }, durationSec * 0.25)
      .to(this, {
        intensity: this.maxIntensity,
        duration: durationSec,
        ease: "power2.out"
      }, 0);

    return this.enterTimeline;
  }

  // The burn keeping itself alive. There is no loop: the puffs are born and die on
  // their own timings, so it never repeats and never needs to.
  startAmbient() {
    if (this.running) return;

    this.running = true;
    this.elapsed = 0;
    gsap.ticker.add(this.tick);
  }

  stopAmbient() {
    if (!this.running) return;

    this.running = false;
    gsap.ticker.remove(this.tick);
  }


  // Leaving the stage behind. The burn keeps running underneath so it is still alive
  // while it fades, which reads better than a freeze.
  buildExit(durationSec = 1) {
    this.killTimelines();

    this.exitTimeline = gsap.timeline({
      onComplete: () => this.reset()
    });

    this.exitTimeline.to([this.$glow, this.$haze], {
      autoAlpha: 0,
      duration: durationSec,
      ease: "power1.inOut"
    });

    return this.exitTimeline;
  }

  killTimelines() {
    if (this.enterTimeline) this.enterTimeline.kill();
    if (this.exitTimeline) this.exitTimeline.kill();

    this.enterTimeline = null;
    this.exitTimeline = null;
  }

  kill() {
    this.reset();
  }
}
